// Gift Redemption ("Claim My Gift") service - PT-2616
//
// Access is authorized entirely by the signed query string (signature/expires/v) that
// arrives on the URL from the emailed link - it is forwarded as-is to the backend on every
// call. There is no session/auth involved, unlike the rest of the storefront's API calls.
import { fetchWithErrorHandling } from './apiUtils';
import { CheckoutField } from '../types';
import { ApiError } from '../errors/ApiError';

export interface GiftRedemptionVariation {
  id: number;
  color: string | null;
  hex: string | null;
  size: string | null;
}

export interface GiftRedemptionProduct {
  id: number;
  name: string;
  description: string | null;
  images: string[];
  variations: GiftRedemptionVariation[];
}

// Whatever a prior visit's autosave left on the recipient row (see GiftRedemptionPage.tsx's
// debounced/immediate saveDraft() calls) - resolve() returns this so re-opening the same link
// resumes where the recipient left off instead of starting the form over blank.
export interface GiftRedemptionDraftData {
  productId: number | null;
  color: string | null;
  size: string | null;
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  postalcode: string | null;
  checkoutFieldValues: Record<string, string>;
}

export interface GiftRedemptionPageData {
  programName: string | null;
  // Set from nova-kit's Edit Program Details modal - null renders nothing (no fallback copy).
  heading: string | null;
  subheading: string | null;
  shipByMessage: string | null;
  recipientName: string | null;
  recipientPhone: string | null;
  products: GiftRedemptionProduct[];
  checkoutFields: CheckoutField[];
  draft: GiftRedemptionDraftData;
}

export interface GiftRedemptionConfirmationData {
  gift: { name: string; color: string | null; size: string | null; imageUrl: string | null } | null;
  shippingAddress: {
    name: string | null;
    address1: string | null;
    address2: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
  };
  shipByMessage: string | null;
}

export interface GiftClaimPayload {
  productId: number;
  color: string | null;
  size: string | null;
  recipientName: string;
  address1: string;
  address2: string | null;
  city: string;
  state: string | null;
  postalcode: string | null;
  countrycode: string;
  phone: string;
  checkoutFieldValues: Record<string, string>;
}

// Adds email - the self-claim flow has no pre-existing recipient to read it from, so the
// person provides it themselves; it's what selfClaim() uses to create their recipient row.
export interface GiftSelfClaimPayload extends GiftClaimPayload {
  email: string;
}

// Same shape as GiftClaimPayload, but every field nullable - a draft autosave fires before the
// recipient has necessarily filled in the whole form (see GiftRedemptionPage.tsx).
export interface GiftClaimDraftPayload {
  productId: number | null;
  color: string | null;
  size: string | null;
  recipientName: string | null;
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  postalcode: string | null;
  countrycode: string | null;
  phone: string | null;
  checkoutFieldValues: Record<string, string>;
}

// Thrown by resolve() when the recipient already claimed - the backend returns this as a
// normal 200 (the link's signature is still valid; a truly expired/tampered link is a 404,
// handled as a plain ApiError below), just shaped like GiftRedemptionConfirmationData instead
// of GiftRedemptionPageData. Re-thrown here as an error so the page's existing catch-driven
// confirmation-screen branch (see GiftRedemptionPage.tsx) doesn't need to know about the
// shape-detection - it still just catches GiftAlreadyClaimedError.
export class GiftAlreadyClaimedError extends Error {
  constructor(public readonly confirmation: GiftRedemptionConfirmationData) {
    super("You've already claimed your gift! Check your email for your confirmation details.");
    this.name = 'GiftAlreadyClaimedError';
  }
}

export class GiftRedemptionService {
  static async resolve(recipientId: string, search: string): Promise<GiftRedemptionPageData> {
    const httpResponse = await fetch(`/api/gift/${recipientId}${search}`);
    const body = await httpResponse.json();

    if (!httpResponse.ok) {
      throw new ApiError(body.message ?? 'This link is not valid or has expired.', httpResponse.status);
    }

    // An already-claimed recipient gets GiftRedemptionConfirmationData back instead - it has
    // a `gift` key, which GiftRedemptionPageData never does.
    if ('gift' in body.data) {
      throw new GiftAlreadyClaimedError(body.data as GiftRedemptionConfirmationData);
    }

    return (body.data as GiftRedemptionPageData);
  }

  static async claim(
    recipientId: string,
    search: string,
    payload: GiftClaimPayload
  ): Promise<GiftRedemptionConfirmationData> {
    const response = await fetchWithErrorHandling<{ data: GiftRedemptionConfirmationData }>(
      // Same path as resolve() - the signature is only valid for the exact path it was
      // generated for, not any given HTTP method, so GET and POST share one path.
      `/api/gift/${recipientId}${search}`,
      { method: 'POST', body: JSON.stringify(payload) },
      { handle422Errors: true }
    );
    return response.data;
  }

  // Autosaves the recipient's in-progress selection without completing the claim - PATCH
  // shares the exact same path as resolve()/claim() (see routes/gift_redemption.php's comment
  // on why: the signature only covers scheme+host+path+query, never the verb). Callers are
  // expected to treat a rejected promise as non-fatal (this is a background autosave, not the
  // real submission) - see GiftRedemptionPage.tsx's use of this.
  static async saveDraft(recipientId: string, search: string, payload: GiftClaimDraftPayload): Promise<void> {
    await fetchWithErrorHandling(`/api/gift/${recipientId}${search}`, { method: 'PATCH', body: JSON.stringify(payload) });
  }

  // Resolves a program's shared claim code (see GiftProgram.use_short_code in nova-kit) to a
  // signed self-claim URL - the code identifies the program only, not a person; the caller just
  // redirects the browser there, where a fresh recipient is created on submit (see selfClaim()).
  static async resolveByCode(code: string): Promise<{ claimUrl: string }> {
    return fetchWithErrorHandling<{ claimUrl: string }>(
      '/api/gift-code/resolve',
      { method: 'POST', body: JSON.stringify({ code }) },
      { handle422Errors: true }
    );
  }

  // Same response shape as resolve() (recipientName/recipientPhone/draft just come back null),
  // reached via nova-kit's "Preview" button (see ProgramHeader.vue) rather than an emailed
  // recipient link - see GiftRedemptionLinkService::forProgramPreview().
  static async resolvePreview(programId: string, search: string): Promise<GiftRedemptionPageData> {
    const httpResponse = await fetch(`/api/gift/preview/${programId}${search}`);
    const body = await httpResponse.json();

    if (!httpResponse.ok) {
      throw new ApiError(body.message ?? 'This preview link is not valid or has expired.', httpResponse.status);
    }

    return body.data as GiftRedemptionPageData;
  }

  // Same response shape as resolvePreview() - no recipient exists yet, reached via the signed
  // link resolveByCode() above returns.
  static async resolveSelfClaimProgram(programId: string, search: string): Promise<GiftRedemptionPageData> {
    const httpResponse = await fetch(`/api/gift/self/${programId}${search}`);
    const body = await httpResponse.json();

    if (!httpResponse.ok) {
      throw new ApiError(body.message ?? 'This link is not valid or has expired.', httpResponse.status);
    }

    return body.data as GiftRedemptionPageData;
  }

  // Creates the recipient (by email, see GiftRedemptionService::selfClaim()) and claims a gift
  // for them in the same request - there is no separate "resolve the existing recipient" step.
  static async selfClaim(
    programId: string,
    search: string,
    payload: GiftSelfClaimPayload
  ): Promise<GiftRedemptionConfirmationData> {
    const response = await fetchWithErrorHandling<{ data: GiftRedemptionConfirmationData }>(
      `/api/gift/self/${programId}${search}`,
      { method: 'POST', body: JSON.stringify(payload) },
      { handle422Errors: true }
    );
    return response.data;
  }
}
